import React from 'react'
import { useIntl } from 'react-intl'
import { Column } from '../../legacy-design-system/grid'
import { PureQueryOptions } from 'apollo-client'
import {
  VulnerabilityDateField,
  VulnerabilityOrSinkGroup,
  VulnerabilityProtectStatus
} from './shared'
import {
  getSeverityCellRenderer,
  loadingVulnerabilitySeverityCellRenderer
} from './cell-renderers/severity'
import {
  getVulnerabilityCellRenderer,
  loadingVulnCellRenderer
} from './cell-renderers/vulnerability'
import {
  getApplicationCellRenderer,
  loadingApplicationCellRenderer
} from './cell-renderers/application'
import {
  getProtectStatusCellRenderer,
  loadingProtectStatusCellRenderer
} from './cell-renderers/protect-status'
import {
  getStatusCellRenderer,
  loadingStatusCellRenderer
} from './cell-renderers/status'
import {
  getDateDetectedCellRenderer,
  loadingLastDetectedCellRenderer
} from './cell-renderers/date-detected'
import { getRowActionsCellRenderer } from './cell-renderers/row-action'
import {
  getSessionMetadataCellRenderer,
  loadingSessionMetadataCellRenderer
} from './cell-renderers/session-metadata'

import {
  CustomApplicationAggregation,
  metadataHeaderRenderer as appMetadataHeaderRenderer
} from '../applications-grid'
import { DateRangeOption } from '../../legacy-design-system/date-picker'
import { DateRange } from '../../legacy-design-system/date-picker/shared'
import { Role } from '@contrast-security-inc/base-types'
import { IconInformation } from '@contrast-security-inc/design-system-foundations'
import { Tooltip } from '@contrast-security-inc/design-system-popovers'
import { getLastDetectedHeaderRenderer } from './date-detected-header'
import { metadataHeaderRenderer } from './metadata-header'
import { AgentFeature } from '../agent-features'
import { SessionMetadataAggregation } from '../session-metadata-aggregation'
import { MetadataFilter } from '../filter-manager/metadata-filter-manager'

interface ColumnGetterArgs<TAggregationCategory extends string> {
  readonly aggregations?: TAggregationCategory[]
}

export type ColumnGetter<
  TAggregationCategory extends string,
  TAdditionalArgs = {}
> = (
  args: ColumnGetterArgs<TAggregationCategory> & TAdditionalArgs
) => Column<VulnerabilityOrSinkGroup, TAggregationCategory>

export interface SeverityColumnArgs {
  readonly organizationID: string
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
}

export function getSeverityColumn<TAggregationCategory extends string>({
  aggregations,
  organizationID,
  refetchQueries,
  refresh
}: ColumnGetterArgs<TAggregationCategory> & SeverityColumnArgs): Column<
  VulnerabilityOrSinkGroup,
  TAggregationCategory
> {
  return {
    cellRenderer: getSeverityCellRenderer({
      organizationID,
      refetchQueries,
      refresh
    }),
    loadingCellRenderer: loadingVulnerabilitySeverityCellRenderer,
    headerConfig: {
      message: {
        id: 'title_severityLevel',
        defaultMessage: 'Severity'
      },
      aggregations
    },
    width: 120,
    minWidth: 120,
    maxWidth: 120,
    horizontalCenter: false
  }
}

export interface VulnerabilityColumnArgs {
  dateField?: string
  organizationID: string
  organizationRole: Role
  hasLicenses: boolean
  features: AgentFeature[]
  refetchQueries?: PureQueryOptions[]
  refresh?: () => Promise<any>
  getDrilldownPath: (id: string) => string
  protectAssessEnv?: string
  isGroupBySinkEnabled?: boolean
  isProtectColumnEnabled?: boolean
  isProtectEnabled?: boolean
}

export function getVulnerabilityColumn<TAggregationCategory extends string>({
  aggregations,
  organizationID,
  organizationRole,
  hasLicenses,
  features,
  dateField,
  getDrilldownPath,
  refresh,
  refetchQueries
}: ColumnGetterArgs<TAggregationCategory> & VulnerabilityColumnArgs): Column<
  VulnerabilityOrSinkGroup,
  TAggregationCategory
> {
  return {
    cellRenderer: getVulnerabilityCellRenderer({
      organizationID,
      organizationRole,
      hasLicenses,
      features,
      dateField,
      getDrilldownPath,
      refresh,
      refetchQueries
    }),
    loadingCellRenderer: loadingVulnCellRenderer,
    headerConfig: {
      message: {
        id: 'title_vulnerability'
      },
      aggregations
    },
    width: 400,
    minWidth: 300,
    grow: 3,
    horizontalCenter: false
  }
}

export interface ApplicationColumnArgs {
  organizationID: string
  organizationRole: Role
  hasLicenses: boolean
  features: AgentFeature[]
  renderLink?: boolean
  applicationInfo?: { primary: boolean }
  customAggregations?: CustomApplicationAggregation[]
  metadataSelections?: MetadataFilter[]
  onMetadataOptionsToggle?: (groupID: string, values: string[]) => void
  onClearMetadataSelections?: () => void
}

export function getApplicationColumn<TAggregationCategory extends string>({
  organizationID,
  organizationRole,
  hasLicenses,
  features,
  aggregations,
  renderLink,
  applicationInfo,
  customAggregations = [],
  metadataSelections = [],
  onMetadataOptionsToggle = () => null,
  onClearMetadataSelections = () => null
}: ColumnGetterArgs<TAggregationCategory> & ApplicationColumnArgs): Column<
  VulnerabilityOrSinkGroup,
  TAggregationCategory
> {
  return {
    cellRenderer: getApplicationCellRenderer({
      organizationID,
      organizationRole,
      hasLicenses,
      features,
      renderLink
    }),
    loadingCellRenderer: loadingApplicationCellRenderer,
    headerConfig: {
      message: {
        id: 'title_application',
        defaultMessage: 'Application'
      },
      aggregations,
      render: (props) =>
        appMetadataHeaderRenderer({
          ...props,
          customAggregations,
          customAggregationSelections: metadataSelections,
          onMetadataOptionsToggle,
          onClearMetadataSelections
        })
    },
    width: 170,
    minWidth: 150,
    maxWidth: 300,
    horizontalCenter: false,
    // the value of this column will only differ for merged applications
    // hide this column when application is not merged
    isHidden: () => !!(applicationInfo && !applicationInfo.primary)
  }
}
export interface ProtectStatusColumnArgs {
  organizationID: string
  refetchQueries?: PureQueryOptions[]
  refresh?: () => Promise<any>
  protectAssessEnv: string
}

export interface VulnerabilityProtectColumnArgs {
  aggregations
  protectAssessEnv?: string
  isGroupBySinkEnabled?: boolean
  isProtectColumnEnabled?: boolean
  isProtectEnabled?: boolean
}

export function getProtectStatusColumn<TAggregationCategory extends string>({
  aggregations,
  protectAssessEnv,
  isProtectEnabled
}: ColumnGetterArgs<TAggregationCategory> &
  VulnerabilityProtectColumnArgs): Column<
  VulnerabilityProtectStatus,
  TAggregationCategory
> {
  return {
    cellRenderer: getProtectStatusCellRenderer({
      protectAssessEnv
    }),
    loadingCellRenderer: loadingProtectStatusCellRenderer,
    headerConfig: {
      aggregations,
      message: {
        id: 'title_protectedInEnv',
        // @ts-ignore
        values: {
          env: protectAssessEnv,
          icon: () => {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const intl = useIntl()

            return (
              <Tooltip
                interactive={true}
                style={{ maxWidth: '260px' }}
                body={intl.formatMessage(
                  { id: 'title_protectStatusTooltip' },
                  { env: protectAssessEnv }
                )}
              >
                <IconInformation
                  size="sm"
                  style={{ verticalAlign: 'text-bottom' }}
                />
              </Tooltip>
            )
          }
        }
      }
    },
    width: 180,
    minWidth: 236,
    maxWidth: 260,
    horizontalCenter: true,
    // show when
    // 1) protect is turned on and user has access to it
    isHidden: () => !isProtectEnabled
  }
}

export interface StatusColumnArgs {
  readonly organizationID: string
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
}

export function getStatusColumn<TAggregationCategory extends string>({
  aggregations,
  organizationID,
  refetchQueries,
  refresh
}: ColumnGetterArgs<TAggregationCategory> & StatusColumnArgs): Column<
  VulnerabilityOrSinkGroup,
  TAggregationCategory
> {
  return {
    cellRenderer: getStatusCellRenderer({
      organizationID,
      refetchQueries,
      refresh
    }),
    loadingCellRenderer: loadingStatusCellRenderer,
    headerConfig: {
      message: {
        id: 'title_status',
        defaultMessage: 'Status'
      },
      aggregations
    },
    width: 200,
    minWidth: 230,
    maxWidth: 230,
    horizontalCenter: false
  }
}

export interface LastDetectedColumnArgs {
  readonly dateRange?: DateRange
  readonly dateRangeOption?: DateRangeOption
  readonly onSetCustomDateRange: (dateRange: DateRange) => void
  readonly onSetDateRangeOption: (dateRangeOption: DateRangeOption) => void
  readonly onClearDateRange: () => void
  readonly dateField?: VulnerabilityDateField
  readonly onSetDateField: (dateField: VulnerabilityDateField) => void
}

export function getDateDetectedColumn<TAggregationCategory extends string>({
  dateRange,
  dateRangeOption,
  onSetCustomDateRange,
  onSetDateRangeOption,
  onClearDateRange,
  dateField,
  onSetDateField
}: LastDetectedColumnArgs): Column<
  VulnerabilityOrSinkGroup,
  TAggregationCategory
> {
  return {
    cellRenderer: getDateDetectedCellRenderer({ dateField }),
    loadingCellRenderer: loadingLastDetectedCellRenderer,
    headerConfig: {
      message: {
        id:
          dateField === 'LAST'
            ? 'title_vulnerabilities_lastDetected_vulnerability'
            : 'title_vulnerabilities_firstDetected_vulnerability'
      },
      render: getLastDetectedHeaderRenderer<TAggregationCategory>({
        dateRange,
        dateRangeOption,
        onSetCustomDateRange,
        onSetDateRangeOption,
        onClearDateRange,
        dateField,
        onSetDateField
      })
    },
    width: 170,
    minWidth: 170,
    maxWidth: 170,
    horizontalCenter: false
  }
}

export interface RowActionsColumnArgs {
  readonly organizationID: string
  readonly organizationRole: Role
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
  readonly trackJob: (job: {
    readonly id: string
    readonly progress: number
    readonly total: number
  }) => Promise<void>
  restrictedEditRole?: boolean
}

export function getRowActionColumn<TAggregationCategory extends string>({
  organizationID,
  refresh,
  refetchQueries,
  trackJob,
  restrictedEditRole
}: ColumnGetterArgs<TAggregationCategory> & RowActionsColumnArgs): Column<
  VulnerabilityOrSinkGroup,
  TAggregationCategory
> {
  return {
    cellRenderer: getRowActionsCellRenderer({
      organizationID,
      refresh,
      refetchQueries,
      trackJob,
      restrictedEditRole
    }),
    loadingCellRenderer: () => <div />,
    maxWidth: 110,
    width: 110,
    minWidth: 110
  }
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

export interface SessionMetadataColumnArgs {
  readonly sessionMetadataFieldID?: string
  readonly sessionMetadataAggregations?: SessionMetadataAggregation[]
  readonly metadataSelections?: MetadataFilter[]
  readonly onClearMetadataSelections?: () => void
  readonly onMetadataOptionsToggle?: (
    groupID: string,
    optionIDs: string[]
  ) => void
  readonly aggregationSelections: { agentSessionId?: string }
}

export function getSessionMetadataColumn<TAggregationCategory extends string>({
  aggregationSelections,
  sessionMetadataFieldID,
  sessionMetadataAggregations = [],
  metadataSelections = [],
  onClearMetadataSelections = noop,
  onMetadataOptionsToggle = noop
}: ColumnGetterArgs<TAggregationCategory> & SessionMetadataColumnArgs): Column<
  VulnerabilityOrSinkGroup,
  TAggregationCategory
> {
  return {
    cellRenderer: getSessionMetadataCellRenderer({ sessionMetadataFieldID }), // <--- HERE expected to be always defined
    loadingCellRenderer: loadingSessionMetadataCellRenderer,
    headerConfig: {
      message: {
        id: 'title_sessionMetadata_sessionColumnHeader'
      },
      render: (props) =>
        metadataHeaderRenderer({
          ...props,
          metadataAggregations: sessionMetadataAggregations,
          metadataSelections,
          onMetadataOptionsToggle,
          onClearMetadataSelections,
          isDrilldown: true
        })
    },
    width: 170,
    minWidth: 170,
    maxWidth: 170,
    horizontalCenter: false,
    isHidden: () =>
      !sessionMetadataFieldID ||
      sessionMetadataFieldID === 'dissociated' || // hide column when dissociated type is selected
      !!aggregationSelections.agentSessionId // hide column when session metadata filter is active for grid
  }
}
